import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from '@mui/x-date-pickers';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DrawerLayoutWrapper, MDialog } from 'common';
import { DrawerContext } from 'common/drawer/DrawerContext';
import { useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';
import { formatISO, isBefore, startOfDay, startOfToday } from 'date-fns';
import { Chip } from '@mui/material';
import * as R from 'ramda';
import {
  useGetAffiliateProductsQuery,
  useGetDestinationsQuery,
  useGetFromLocationListQuery,
  useGetProductOriginsQuery,
  useGetReidVaporPressureQuery,
} from '../reconsignmentsEndpoints';

const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.originName}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const initialState = {
  fromLocation: null,
  inventoryOwner: null,
  origin: [],
  product: null,
  rvp: null,
  toLocation: null,
  transferDate: null,
};

const ConfigurationStep = ({
  next,
  reconsignmentState,
  setReconsignmentState,
  openDialog,
  setOpenDialog,
}) => {
  // TODO: Make use of props: onClose, prev, reconsignmentState, setReconsignmentState

  const { setDrawerComponentProps } = useContext(DrawerContext);
  const { fromLocation, inventoryOwner, origin, product, rvp, toLocation, transferDate } =
    reconsignmentState;
  const { affiliates } = useContext(AuthContext);
  const { currentAffiliate } = useUserRolePermissions();
  const validStartDate = startOfDay(new Date());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [defaultValueChanged, setDefaultValueChanged] = useState(false);
  const transferDateValue = R.isNil(transferDate) ? validStartDate : new Date(transferDate);
  const handleDrawerClose = () => {
    setDrawerComponentProps({
      Component: '',
      forceCloseSelector: () => false,
      holdOpenSelector: () => false,
      refreshNotifications: false,
      resetWizard: undefined,
      setCurrentDialogOpen: () => {},
      type: '',
    });
  };

  useEffect(() => {
    if (R.isNil(inventoryOwner) && R.isNil(transferDate)) {
      setReconsignmentState(
        R.mergeLeft(
          {
            inventoryOwner: currentAffiliate,
            transferDate: formatISO(validStartDate),
          },
          reconsignmentState
        )
      );
    }
  }, [
    currentAffiliate,
    inventoryOwner,
    reconsignmentState,
    setReconsignmentState,
    transferDate,
    validStartDate,
  ]);

  useEffect(() => {
    if (affiliates.length === 1) {
      setReconsignmentState({
        ...reconsignmentState,
        inventoryOwner: currentAffiliate,
        transferDate: transferDate ?? formatISO(validStartDate),
      });
    }
  }, [
    affiliates,
    currentAffiliate,
    reconsignmentState,
    setReconsignmentState,
    transferDate,
    validStartDate,
  ]);

  const handleClose = () => {
    // transferdate, inventoryowner is auto-selected, so we can't include it
    // in state comparisons, or it'll never be equal to initial state.
    const omitFields = R.omit(['transferDate', 'inventoryOwner']);
    if (
      R.equals(omitFields(reconsignmentState), omitFields(initialState)) &&
      !defaultValueChanged
    ) {
      handleDrawerClose();
    } else {
      setOpenDialog(true);
    }
  };

  const [validationState, setValidationState] = useState({
    transferDate: '',
  });

  const params = {
    inventoryOwner: {
      companyCustomerIdName: inventoryOwner?.customerIdName,
      dataCompanyId: inventoryOwner?.dataCompanyId,
    },
  };

  const { data: products = [], isFetching: isLoadingProducts } = useGetAffiliateProductsQuery(
    { params },
    { skip: R.isNil(inventoryOwner) }
  );

  const isGasoline = R.equals(R.T(), R.prop('isGasoline', product));
  const rvpParams = R.assoc('productId', R.prop('productId', product), params);

  const { data: rvpData = [], isFetching: isLoadingRVP } = useGetReidVaporPressureQuery(
    { params: rvpParams },
    { skip: R.not(isGasoline) }
  );
  const rvps = R.prepend('Any', rvpData);

  const fromLocationParams = R.assoc('rvp', rvp, rvpParams);
  const {
    data: fromLocationQuery = [],
    isFetching: isLoadingFromLocation,
    isError: isErrorFromLocation,
    error: fromLocationError,
  } = useGetFromLocationListQuery({ params: fromLocationParams }, { skip: R.isNil(product) });

  const destinationParams = R.assoc('locationId', fromLocation?.locationId, rvpParams);

  const { data: destinationsData = [], isFetching: isLoadingDestinations } =
    useGetDestinationsQuery(
      { params: destinationParams },
      {
        skip: R.or(R.isNil(currentAffiliate), R.isNil(fromLocation)),
      }
    );

  const originParams = R.pickBy(R.identity, {
    'InventoryOwner.CompanyCustomerIdName': inventoryOwner?.customerIdName,
    'InventoryOwner.DataCompanyId': inventoryOwner?.dataCompanyId,
    LocationId: fromLocation?.locationId,
    ProductId: product?.productId,
    RVP: rvp && rvp !== 'Any' ? Number(rvp) : null,
  });

  const {
    data: rawOriginsData = [],
    isFetching: isLoadingOrigin,
    isError: isErrorOrigin,
  } = useGetProductOriginsQuery({ params: originParams }, { skip: R.isNil(fromLocation) });

  const originsData = useMemo(() => {
    return isErrorOrigin ? [] : rawOriginsData ?? [];
  }, [isErrorOrigin, rawOriginsData]);

  const validateTransferDate = date => {
    if (R.isNil(date)) {
      setValidationState(R.assoc('transferDate', 'Date required'));
    } else if (date.toString() === 'Invalid Date') {
      setValidationState(R.assoc('transferDate', 'Invalid Date'));
    } else if (isBefore(new Date(date), startOfToday())) {
      setValidationState(R.assoc('transferDate', 'Transfer Date must be today or later'));
    } else {
      setValidationState(R.assoc('transferDate', ''));
    }
  };

  useEffect(() => {
    if (isErrorFromLocation) {
      setReconsignmentState(
        R.mergeLeft(
          {
            fromLocation: null,
            fullFilment: null,
            origin: [],
          },
          reconsignmentState
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorFromLocation, fromLocationError, setReconsignmentState]);

  const handleTransferDateChange = value => {
    validateTransferDate(value);
    const date = R.tryCatch(val => {
      if (val.getUTCFullYear() < 1000) {
        return null;
      }
      return formatISO(val);
    }, R.always(null))(value);
    setReconsignmentState({
      ...reconsignmentState,
      fullFilment: null,
      transferDate: date,
      volume: '',
    });
    setDefaultValueChanged(true);
  };

  const handleInventoryOwnerChange = value => {
    const affiliateIndex = affiliates.findIndex(
      option => option.customerIdName === value.customerIdName
    );

    const selectedAffiliate = affiliates[affiliateIndex];
    setReconsignmentState(
      R.mergeLeft(
        {
          fromLocation: null,
          fullFilment: null,
          inventoryOwner: selectedAffiliate,
          origin: [],
          product: null,
          rvp: null,
          toLocation: null,
        },
        reconsignmentState
      )
    );
    setDefaultValueChanged(true);
  };
  const handleProductChange = value => {
    const rvpValue = R.prop('isGasoline', value) ? 'Any' : null;
    setReconsignmentState({
      ...reconsignmentState,
      fromLocation: null,
      fullFilment: null,
      origin: [],
      product: value,
      rvp: rvpValue,
      toLocation: null,
    });
  };

  const handleRVPChange = value => {
    setReconsignmentState({
      ...reconsignmentState,
      fromLocation: null,
      fullFilment: null,
      origin: [],
      rvp: value,
    });
  };

  const handleFromLocationChange = value => {
    setReconsignmentState({
      ...reconsignmentState,
      fromLocation: value,
      fullFilment: null,
      origin: [],
      toLocation: null,
    });
  };

  const handleToLocationChange = value => {
    setReconsignmentState({
      ...reconsignmentState,
      fullFilment: null,
      toLocation: value,
      volume: '', // Reset volume
    });
  };

  const handleOriginChange = value => {
    setReconsignmentState({
      ...reconsignmentState,
      fullFilment: null,
      origin: value,
      volume: '',
    });
  };

  useEffect(() => {
    if (isGasoline && !R.isNil(product) && R.isNil(rvp)) {
      setReconsignmentState({ ...reconsignmentState, rvp: rvps[0] });
    }
  }, [isGasoline, product, rvp, rvps, reconsignmentState, setReconsignmentState]);

  const isInvalid = [
    R.isNil(inventoryOwner),
    R.isNil(product),
    R.and(isGasoline, R.isNil(rvp)),
    R.isNil(fromLocation),
    R.isNil(toLocation),
    R.not(R.isEmpty(validationState.transferDate)),
  ].includes(true);

  const errorValidate = !R.isEmpty(validationState.transferDate);
  const helperTextValidate = validationState.transferDate;

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={handleClose} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <>
      <DrawerLayoutWrapper
        buttons={actionButtons}
        headerButtonText="CLOSE"
        onHeaderButtonClick={handleClose}
        title="New 1 to 1 Reconsignment"
      >
        <Box
          sx={{
            borderBottom: 1,
            borderBottomColor: 'graphic.light',
            p: 3,
            width: 390,
          }}
        >
          <LinearProgress data-variant="magellanProgressBar" value={33} variant="determinate" />
          <Typography>Configuration (1 of 3)</Typography>
        </Box>
        <Stack sx={{ display: 'grid', gap: 3, m: 3 }}>
          <DatePicker
            defaultValue={validStartDate}
            disableMaskedInput
            inputFormat="MMMM dd, yyyy"
            inputProps={{ readOnly: true }}
            label="Transfer Date"
            minDate={validStartDate}
            onAccept={() => setIsDatePickerOpen(false)}
            onChange={date => {
              handleTransferDateChange(date);
            }}
            onClose={() => setIsDatePickerOpen(false)}
            open={isDatePickerOpen}
            openTo="day"
            slotProps={{
              textField: {
                error: errorValidate,
                helperText: helperTextValidate,
                onClick: () => setIsDatePickerOpen(true),
              },
            }}
            value={transferDateValue}
          />
          {affiliates.length > 1 && (
            <Autocomplete
              disableClearable
              getOptionLabel={o =>
                R.isEmpty(inventoryOwner) ? '' : R.prop('customerNameShort', o)
              }
              isOptionEqualToValue={(option, value) =>
                R.isEmpty(inventoryOwner) ? false : R.eqProps('customerIdName')(option, value)
              }
              onChange={(__, value) => handleInventoryOwnerChange(value)}
              options={affiliates}
              renderInput={inputParams => <TextField {...inputParams} label="Inventory Owner" />}
              value={R.defaultTo(affiliates[0], inventoryOwner)}
            />
          )}
          <Autocomplete
            className="css-heap-redact"
            disableClearable
            getOptionLabel={o => (R.isEmpty(product) ? '' : R.prop('productName', o))}
            isOptionEqualToValue={(option, value) =>
              R.isEmpty(product) ? false : R.eqProps('productId')(option, value)
            }
            loading={isLoadingProducts}
            onChange={(_, value) => handleProductChange(value)}
            options={products}
            renderInput={inputParams => <TextField {...inputParams} label="Product" />}
            value={product}
          />
          <Autocomplete
            disableClearable
            getOptionLabel={o => (R.isEmpty(rvp) ? '' : o.toString())}
            hidden={!isGasoline}
            loading={isLoadingRVP}
            onChange={(_, value) => handleRVPChange(value)}
            options={rvps}
            renderInput={inputParams => <TextField {...inputParams} label="RVP" />}
            value={R.defaultTo('Any', rvp)}
          />
          <Autocomplete
            disableClearable
            disabled={R.isNil(product)}
            getOptionLabel={o => (R.isEmpty(fromLocation) ? '' : R.prop('locationName', o))}
            isOptionEqualToValue={(option, value) =>
              R.isEmpty(fromLocation) ? false : R.eqProps('locationId')(option, value)
            }
            loading={isLoadingFromLocation}
            onChange={(_, value) => handleFromLocationChange(value)}
            options={isErrorFromLocation ? [] : fromLocationQuery}
            renderInput={inputParams => <TextField {...inputParams} label="From Location" />}
            value={fromLocation}
          />
          <Autocomplete
            disableClearable
            disabled={R.isNil(product) || R.isNil(fromLocation)}
            getOptionLabel={o => (R.isEmpty(toLocation) ? '' : R.prop('locationName', o))}
            isOptionEqualToValue={(option, value) =>
              R.isEmpty(toLocation) ? false : R.eqProps('locationId')(option, value)
            }
            loading={isLoadingDestinations}
            onChange={(_, value) => handleToLocationChange(value)}
            options={destinationsData}
            renderInput={inputParams => <TextField {...inputParams} label="To Location" />}
            value={toLocation}
          />
          <Autocomplete
            disabled={R.isNil(fromLocation)}
            getOptionLabel={o => R.defaultTo('', R.prop('originName', o))}
            isOptionEqualToValue={R.eqProps('originId')}
            loading={isLoadingOrigin}
            multiple
            onChange={(_, value) => handleOriginChange(value)}
            options={originsData}
            renderInput={inputParams => (
              <TextField {...inputParams} helperText="(Optional)" label="Origin" />
            )}
            renderTags={renderTags}
            value={origin}
          />
        </Stack>
      </DrawerLayoutWrapper>
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={() => {
          setOpenDialog(false);
          handleDrawerClose();
        }}
        leftButtonText="Continue"
        open={openDialog}
        rightButtonAction={() => setOpenDialog(false)}
        rightButtonText="Keep Editing"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved one to one reconsignment
          changes.
        </Typography>
      </MDialog>
    </>
  );
};

ConfigurationStep.propTypes = {
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  openDialog: PropTypes.bool.isRequired,
  prev: PropTypes.func.isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({
      locationId: PropTypes.number,
      locationName: PropTypes.string,
    }),
    inventoryOwner: PropTypes.shape({
      customerIdName: PropTypes.string,
      customerNameShort: PropTypes.string,
      dataCompanyId: PropTypes.number,
    }),
    origin: PropTypes.arrayOf(
      PropTypes.shape({
        originId: PropTypes.number,
        originName: PropTypes.string,
      })
    ),
    product: PropTypes.shape({
      productId: PropTypes.number,
      productName: PropTypes.string,
    }),
    rvp: PropTypes.number,
    toLocation: PropTypes.shape({
      locationId: PropTypes.number,
      locationName: PropTypes.string,
    }),
    transferDate: PropTypes.string,
  }).isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  setReconsignmentState: PropTypes.func.isRequired,
};

export default ConfigurationStep;
